import { reportAppEvent, type ReportAppEventResponse, type ReportAppEventPayload } from '../api';
import { IPC } from '../../common/ipc/channels';
import {
  APP_REPORT_EVENTS,
  APP_REPORT_EVENT_TYPES,
  type AppReportEvent,
  type AppReportEventType,
} from '../../common/enums/appReport';
import type { AppReportRuntimeInfo } from '../../common/types/appReport';
import { useAppStore } from '../store';

type AppReportProperties = Record<string, unknown>;

interface TrackEventOptions {
  event: AppReportEvent;
  eventType: AppReportEventType;
  properties?: AppReportProperties;
  page?: string;
  referrer?: string;
}

const REPORT_CURRENT_PAGE_STORAGE_KEY = 'app-report:current-page';
const REPORT_PREVIOUS_PAGE_STORAGE_KEY = 'app-report:previous-page';

let runtimeInfoPromise: Promise<AppReportRuntimeInfo | null> | null = null;

function readSessionStorageValue(key: string) {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.sessionStorage.getItem(key)?.trim() || '';
  } catch {
    return '';
  }
}

function writeSessionStorageValue(key: string, value: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!value) {
      window.sessionStorage.removeItem(key);
      return;
    }

    window.sessionStorage.setItem(key, value);
  } catch {
    // 忽略存储异常，避免影响主业务流程。
  }
}

function normalizePagePath(value?: string) {
  const trimmedValue = value?.trim() || '';
  if (!trimmedValue) {
    return '';
  }

  return trimmedValue.startsWith('/') ? trimmedValue : `/${trimmedValue}`;
}

function resolveCurrentPagePath() {
  if (typeof window === 'undefined') {
    return '/';
  }

  const hashPath = normalizePagePath(window.location.hash.replace(/^#/, ''));
  if (hashPath) {
    return hashPath;
  }

  const pathname = normalizePagePath(window.location.pathname);
  return pathname || '/';
}

function resolveNavigatorDeviceInfo() {
  if (typeof navigator === 'undefined') {
    return {
      deviceModel: '',
      deviceName: '',
    };
  }

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: {
      model?: string;
      platform?: string;
    };
  };

  return {
    deviceModel: navigatorWithUserAgentData.userAgentData?.model?.trim() || '',
    deviceName: navigatorWithUserAgentData.userAgentData?.platform?.trim() || navigator.platform || '',
  };
}

async function getAppRuntimeInfo() {
  if (runtimeInfoPromise) {
    return runtimeInfoPromise;
  }

  if (typeof window === 'undefined' || !window.electronAPI) {
    runtimeInfoPromise = Promise.resolve(null);
    return runtimeInfoPromise;
  }

  runtimeInfoPromise = window.electronAPI.invoke(IPC.GET_APP_REPORT_RUNTIME_INFO)
    .then((result) => result as AppReportRuntimeInfo)
    .catch((error) => {
      console.warn('获取上报运行时信息失败，已降级为空值。', error);
      return null;
    });

  return runtimeInfoPromise;
}

class AppReporter {
  syncRouteSnapshot(page?: string) {
    const nextPage = normalizePagePath(page) || resolveCurrentPagePath();
    const currentPage = readSessionStorageValue(REPORT_CURRENT_PAGE_STORAGE_KEY);

    if (currentPage && currentPage !== nextPage) {
      writeSessionStorageValue(REPORT_PREVIOUS_PAGE_STORAGE_KEY, currentPage);
    }

    writeSessionStorageValue(REPORT_CURRENT_PAGE_STORAGE_KEY, nextPage);
  }

  async reportEvent(options: TrackEventOptions): Promise<ReportAppEventResponse | null> {
    // if (import.meta.env.DEV) {
    //   return null;
    // }

    const runtimeInfo = await getAppRuntimeInfo();
    const navigatorDeviceInfo = resolveNavigatorDeviceInfo();
    const currentPage = normalizePagePath(options.page)
      || readSessionStorageValue(REPORT_CURRENT_PAGE_STORAGE_KEY)
      || resolveCurrentPagePath();
    const referrerPage = normalizePagePath(options.referrer)
      || readSessionStorageValue(REPORT_PREVIOUS_PAGE_STORAGE_KEY);
    const authUserId = useAppStore.getState().auth.userId.trim();

    const payload: ReportAppEventPayload = {
      userId: authUserId || undefined,
      deviceModel: runtimeInfo?.deviceModel || navigatorDeviceInfo.deviceModel,
      deviceName: runtimeInfo?.deviceName || navigatorDeviceInfo.deviceName,
      event: options.event,
      eventType: options.eventType,
      mac: runtimeInfo?.mac || '',
      os: runtimeInfo?.os || '',
      platform: runtimeInfo?.platform || '',
      arch: runtimeInfo?.arch || '',
      appVersion: runtimeInfo?.appVersion || '',
      buildVersion: runtimeInfo?.buildVersion || '',
      page: currentPage,
      referrer: referrerPage,
      timestamp: Date.now(),
      properties: options.properties,
    };

    if (import.meta.env.DEV) {
      console.info('[AppReporter] dispatch', payload);
    }

    try {
      const response = await reportAppEvent(payload);
      if (import.meta.env.DEV) {
        console.info('[AppReporter] success', {
          event: payload.event,
          eventType: payload.eventType,
          response,
        });
      }
      return response;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[AppReporter] failed', {
          event: payload.event,
          eventType: payload.eventType,
          error,
        });
      }
      console.warn(`事件上报失败: ${options.event}`, error);
      return null;
    }
  }

  reportLoginButtonClick(properties?: AppReportProperties) {
    return this.reportEvent({
      event: APP_REPORT_EVENTS.LOGIN_BUTTON_CLICK,
      eventType: APP_REPORT_EVENT_TYPES.LOGIN,
      properties,
    });
  }

  reportLoginCodeSendClick(properties?: AppReportProperties) {
    return this.reportEvent({
      event: APP_REPORT_EVENTS.LOGIN_CODE_SEND_CLICK,
      eventType: APP_REPORT_EVENT_TYPES.LOGIN,
      properties,
    });
  }

  reportLoginEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.LOGIN,
      properties,
    });
  }

  reportNavigationEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.NAVIGATION,
      properties,
    });
  }

  reportAuthEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.AUTH,
      properties,
    });
  }

  reportUpdateEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.UPDATE,
      properties,
    });
  }

  reportAccountEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.ACCOUNT,
      properties,
    });
  }

  reportProductEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.PRODUCT,
      properties,
    });
  }

  reportLeadHunterEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.LEAD_HUNTER,
      properties,
    });
  }

  reportDashboardEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.DASHBOARD,
      properties,
    });
  }

  reportContentScoutEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.CONTENT_SCOUT,
      properties,
    });
  }

  reportCreativeWriterEvent(event: AppReportEvent, properties?: AppReportProperties) {
    return this.reportEvent({
      event,
      eventType: APP_REPORT_EVENT_TYPES.CREATIVE_WRITER,
      properties,
    });
  }
}

export const appReporter = new AppReporter();
